import type { CreateFormData } from "@/components/create/types";
import { listFromValue } from "@/lib/agent/briefText";

export type MergeableField =
  | "color"
  | "fabric"
  | "sleeve"
  | "sleeveAddons"
  | "silhouette"
  | "length"
  | "neckline"
  | "waistline"
  | "prompt"
  | "backPrompt";

export interface DesignBriefReducerOptions {
  userMessage?: string;
}

export interface DesignBriefReducerChange {
  field: MergeableField;
  previous: unknown;
  next: unknown;
  reason: string;
}

export interface DesignBriefReducerResult {
  data: CreateFormData;
  replacements: DesignBriefReducerChange[];
  removals: DesignBriefReducerChange[];
  preserved: MergeableField[];
}

export const DESIGN_BRIEF_REDUCER_FIELDS: MergeableField[] = [
  "color",
  "fabric",
  "sleeve",
  "sleeveAddons",
  "silhouette",
  "length",
  "neckline",
  "waistline",
  "prompt",
  "backPrompt",
];

const NEGATED_PATCH_GUARD_FIELDS = new Set<MergeableField>([
  "color",
  "fabric",
  "sleeve",
  "sleeveAddons",
  "silhouette",
  "neckline",
]);

const ADDITIVE_PATTERNS = /\b(add|also|with|include|plus|along with)\b/i;
const REPLACEMENT_PATTERNS =
  /\b(no[, ]+|not\b|actually\b|instead\b|replace\b|change\b|switch\b|swap\b|choose\b|pick\b|go with\b|settle on\b|let's do\b|lets do\b|make it\b|make the\b|rather than\b)\b/i;
const REMOVAL_PATTERNS = /\b(no|not|remove|without|drop|exclude)\b/i;
const PRICE_SMART_PATTERNS =
  /\b(?:price[-\s]?smart|cost[-\s]?conscious|budget[-\s]?conscious|budget[-\s]?friendly)\b/i;
const PRICE_SMART_GLOBAL_PATTERNS =
  /\b(?:price[-\s]?smart|cost[-\s]?conscious|budget[-\s]?conscious|budget[-\s]?friendly)\b/gi;
const PRICE_SMART_CONTEXT =
  "cost-conscious construction with less skirt volume, lower-cost fabric, no embroidery work, no heavy beadwork or appliques, and a less complex design";
const EXPENSIVE_HANDWORK_FABRIC_PATTERNS =
  /\b(?:sequins?|beaded|beading|embroidery|embroidered|lace|appliques?)\b/i;

const FIELD_ALIASES: Record<MergeableField, RegExp[]> = {
  color: [/\bcolou?r\b/i, /\bblack|white|ivory|red|blue|green|pink|blush\b/i],
  fabric: [/\bfabric\b/i, /\bsatin|lace|tulle|chiffon|silk|crepe|organza\b/i],
  sleeve: [/\bsleeves?\b/i, /\bstraps?\b/i],
  sleeveAddons: [/\bsleeves?\b/i, /\bcuffs?\b/i, /\bstraps?\b/i],
  silhouette: [/\bsilhouette\b/i, /\bskirt\b/i, /\bshape\b/i],
  length: [/\blength\b/i, /\btrain\b/i, /\bhem\b/i],
  neckline: [/\bneckline\b/i, /\bneck\b/i],
  waistline: [/\bwaistline\b/i, /\bwaist\b/i],
  prompt: [/\bdesign\b/i, /\bdress\b/i],
  backPrompt: [/\bback\b/i],
};

const SEMANTIC_ALIAS_GROUPS = [
  ["white", "ivory", "cream", "alabaster", "snow", "porcelain", "soft white"],
  ["gold", "champagne", "gilded"],
];

function hasValue(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some(hasValue);
  }
  return typeof value === "string" ? value.trim().length > 0 : value != null;
}

function normalize(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function uniqueValues(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    const trimmed = value.trim();
    const key = normalize(trimmed);
    if (!trimmed || seen.has(key)) continue;
    seen.add(key);
    result.push(trimmed);
  }

  return result;
}

function valuesEqual(left: unknown, right: unknown): boolean {
  if (Array.isArray(left) || Array.isArray(right)) {
    const leftValues = listFromValue(left);
    const rightValues = listFromValue(right);
    return (
      leftValues.length === rightValues.length &&
      leftValues.every(
        (value, index) => normalize(value) === normalize(rightValues[index])
      )
    );
  }

  return left === right;
}

function removeFromValue(value: unknown, terms: string[]): unknown {
  const normalizedTerms = terms.map(normalize).filter(Boolean);
  if (normalizedTerms.length === 0) return value;

  if (Array.isArray(value)) {
    return value.filter(item => {
      const normalizedItem = normalize(String(item));
      return !normalizedTerms.some(
        term => normalizedItem === term || normalizedItem.includes(term)
      );
    });
  }

  if (typeof value !== "string") return value;

  const remaining = listFromValue(value).filter(part => {
    const normalizedPart = normalize(part);
    return !normalizedTerms.some(
      term => normalizedPart === term || normalizedPart.includes(term)
    );
  });

  return remaining.join(", ");
}

function candidateAliases(candidate: string): string[] {
  const normalizedCandidate = normalize(candidate);
  if (!normalizedCandidate) return [];

  const tokens = normalizedCandidate
    .split(/\s+/)
    .map(token => token.trim())
    .filter(token => token.length >= 3);

  const semanticAliases = SEMANTIC_ALIAS_GROUPS.flatMap(group =>
    group.some(alias => normalizedCandidate.includes(normalize(alias)))
      ? group
      : []
  );

  return uniqueValues([normalizedCandidate, ...tokens, ...semanticAliases]);
}

function isSleevelessValue(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const normalizedValue = normalize(value);
  return (
    normalizedValue.includes("sleeveless") ||
    /\bno\s+sleeves?\b/.test(normalizedValue)
  );
}

function hasSleevelessIntent(message: string): boolean {
  const normalizedMessage = normalize(message);
  return (
    /\b(?:no|without|remove|exclude)\s+sleeves?\b/.test(normalizedMessage) ||
    /\b(?:dont|don\s+t|do\s+not)\s+want\s+(?:no\s+|any\s+)?sleeves?\b/.test(
      normalizedMessage
    )
  );
}

function hasPriceSmartIntent(message: string): boolean {
  return PRICE_SMART_PATTERNS.test(message);
}

function explicitlyRequestedHandwork(message: string): boolean {
  return EXPENSIVE_HANDWORK_FABRIC_PATTERNS.test(message);
}

function normalizePriceSmartPrompt(value: unknown): unknown {
  if (typeof value !== "string") return value;

  const withoutShorthand = value
    .replace(PRICE_SMART_GLOBAL_PATTERNS, "")
    .replace(/\s+,/g, ",")
    .replace(/,\s*,+/g, ",")
    .replace(/\s{2,}/g, " ")
    .trim()
    .replace(/^[,;:.-]\s*/, "")
    .replace(/\s*[,;:.-]$/, "");

  if (normalize(withoutShorthand).includes(normalize(PRICE_SMART_CONTEXT))) {
    return withoutShorthand;
  }

  return withoutShorthand
    ? `${withoutShorthand}; ${PRICE_SMART_CONTEXT}`
    : PRICE_SMART_CONTEXT;
}

function extractNegatedTerms(message: string, candidates: string[]): string[] {
  const terms = new Set<string>();
  const normalizedMessage = normalize(message);

  for (const candidate of candidates) {
    const normalizedCandidate = normalize(candidate);
    if (!normalizedCandidate) continue;

    for (const alias of candidateAliases(candidate)) {
      const patterns = [
        new RegExp(
          `\\b(?:no|not|remove|without|drop|exclude|dislike|hate|dont like|don t like|do not like|dont want|don t want|do not want|not into)(?:\\s+\\w+){0,4}\\s+${alias}\\b`,
          "i"
        ),
        new RegExp(`\\b${alias}\\s+(?:off|removed|out)\\b`, "i"),
      ];

      if (
        patterns.some(pattern => pattern.test(normalizedMessage)) ||
        normalizedMessage.includes(`not ${alias}`) ||
        normalizedMessage.includes(`remove ${alias}`)
      ) {
        terms.add(candidate);
        break;
      }
    }
  }

  return Array.from(terms);
}

function valueContainsRemovalTerm(value: unknown, terms: string[]): boolean {
  const normalizedTerms = terms.flatMap(candidateAliases);
  if (normalizedTerms.length === 0) return false;

  return listFromValue(value).some(part => {
    const normalizedPart = normalize(part);
    return normalizedTerms.some(
      term => normalizedPart === term || normalizedPart.includes(term)
    );
  });
}

function fieldMentioned(message: string, field: MergeableField): boolean {
  return FIELD_ALIASES[field].some(pattern => pattern.test(message));
}

function getKeptFields(message: string): Set<MergeableField> {
  const kept = new Set<MergeableField>();
  const keepClauses = message.match(/\bkeep\b[^,.!?;]*/gi) ?? [];

  for (const clause of keepClauses) {
    const keepOnlyClause = clause.split(
      /\b(?:but|change|instead|replace|remove)\b/i
    )[0];
    for (const field of DESIGN_BRIEF_REDUCER_FIELDS) {
      if (fieldMentioned(keepOnlyClause, field)) {
        kept.add(field);
      }
    }
  }

  if (kept.has("sleeve")) {
    kept.add("sleeveAddons");
  }

  return kept;
}

function shouldReplaceField(message: string, field: MergeableField): boolean {
  if (!REPLACEMENT_PATTERNS.test(message)) return false;
  if (fieldMentioned(message, field)) return true;

  return (
    field === "color" &&
    /\b(actually|instead|make it|change it|switch it)\b/i.test(message)
  );
}

function mergeValue(
  field: MergeableField,
  current: unknown,
  next: unknown,
  additive: boolean
): unknown {
  if (!hasValue(next)) return current;

  if (
    Array.isArray(current) ||
    Array.isArray(next) ||
    (additive && field === "color")
  ) {
    if (additive && hasValue(current)) {
      return uniqueValues([...listFromValue(current), ...listFromValue(next)]);
    }
    return Array.isArray(next) ? uniqueValues(listFromValue(next)) : next;
  }

  if (
    additive &&
    typeof current === "string" &&
    current.trim() &&
    typeof next === "string" &&
    next.trim() &&
    normalize(current) !== normalize(next)
  ) {
    return uniqueValues([
      ...listFromValue(current),
      ...listFromValue(next),
    ]).join(", ");
  }

  return next;
}

function getRemovalCandidates(current: Partial<CreateFormData>): string[] {
  return uniqueValues([
    ...listFromValue(current.color),
    ...listFromValue(current.fabric),
    ...listFromValue(current.sleeve),
    ...listFromValue(current.sleeveAddons),
    ...listFromValue(current.silhouette),
  ]);
}

function getPatchRemovalCandidates(patch: Partial<CreateFormData>): string[] {
  return uniqueValues([
    ...listFromValue(patch.color),
    ...listFromValue(patch.fabric),
    ...listFromValue(patch.sleeve),
    ...listFromValue(patch.sleeveAddons),
    ...listFromValue(patch.silhouette),
    ...listFromValue(patch.neckline),
  ]);
}

export function mergeDesignBriefPatch(
  current: CreateFormData,
  patch: Partial<CreateFormData>,
  options: DesignBriefReducerOptions = {}
): DesignBriefReducerResult {
  const message = options.userMessage ?? "";
  const keptFields = getKeptFields(message);
  const additive =
    ADDITIVE_PATTERNS.test(message) && !REPLACEMENT_PATTERNS.test(message);
  const replacements: DesignBriefReducerChange[] = [];
  const removals: DesignBriefReducerChange[] = [];
  const preserved = Array.from(keptFields);
  const data: CreateFormData = { ...current };
  let removalTerms: string[] = [];
  const removedFields = new Set<MergeableField>();
  const preserveSleeveless =
    hasSleevelessIntent(message) &&
    (isSleevelessValue(patch.sleeve) || isSleevelessValue(current.sleeve));
  const priceSmartIntent = hasPriceSmartIntent(message);

  if (REMOVAL_PATTERNS.test(message)) {
    removalTerms = extractNegatedTerms(message, [
      ...getRemovalCandidates(current),
      ...getPatchRemovalCandidates(patch),
    ]);

    for (const field of [
      "color",
      "fabric",
      "sleeve",
      "sleeveAddons",
    ] as MergeableField[]) {
      if (keptFields.has(field)) continue;
      if (field === "sleeve" && preserveSleeveless) continue;

      const previous = data[field];
      const next = removeFromValue(previous, removalTerms);
      if (!valuesEqual(next, previous)) {
        data[field] = next as never;
        removals.push({
          field,
          previous,
          next,
          reason: "negation",
        });
        removedFields.add(field);
      }
    }
  }

  for (const field of DESIGN_BRIEF_REDUCER_FIELDS) {
    if (!(field in patch) || keptFields.has(field)) continue;

    const nextPatchValue = patch[field];
    if (!hasValue(nextPatchValue)) continue;
    if (
      removalTerms.length > 0 &&
      NEGATED_PATCH_GUARD_FIELDS.has(field) &&
      !(field === "sleeve" && preserveSleeveless) &&
      valueContainsRemovalTerm(nextPatchValue, removalTerms)
    ) {
      continue;
    }

    const previous = data[field];
    const replace = shouldReplaceField(message, field);
    let next = replace
      ? nextPatchValue
      : mergeValue(field, previous, nextPatchValue, additive);

    if (priceSmartIntent && field === "prompt") {
      next = normalizePriceSmartPrompt(next);
    }

    if (
      priceSmartIntent &&
      field === "fabric" &&
      typeof next === "string" &&
      EXPENSIVE_HANDWORK_FABRIC_PATTERNS.test(next) &&
      !explicitlyRequestedHandwork(message)
    ) {
      next = "Cotton Blend - Breathable, budget-friendly everyday fabric";
    }

    if (valuesEqual(next, previous)) continue;

    data[field] = next as never;

    if (replace && hasValue(previous)) {
      replacements.push({
        field,
        previous,
        next,
        reason: "replacement",
      });
    }
  }

  if (priceSmartIntent && !("prompt" in patch)) {
    const previous = data.prompt;
    const next = normalizePriceSmartPrompt(previous);
    if (!valuesEqual(next, previous)) {
      data.prompt = next as string;
    }
  }

  return {
    data,
    replacements,
    removals,
    preserved,
  };
}
